import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Printable patient registration form — matches the physical Siddharth Dental Hospital form.
 * Bilingual Hindi/English with checkboxes for medical history, habits, pregnancy, etc.
 */
const RegistrationFormPrint = ({ patient, clinic, onClose }) => {
  const printRef = useRef(null);

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '___________';

  // Medical conditions with Hindi labels (matching physical form)
  const medConditions = [
    { en: 'Diabetes', hi: 'डायबिटीज' },
    { en: 'High B.P.', hi: 'हाई ब्लड प्रेशर' },
    { en: 'Heart Disease', hi: 'हृदय रोग' },
    { en: 'Asthma', hi: 'अस्थमा' },
    { en: 'Epilepsy', hi: 'मिर्गी/दौरे' },
    { en: 'Thyroid', hi: 'थायराइड' },
    { en: 'Liver/Kidney Disease', hi: 'लिवर/किडनी की बीमारी' },
    { en: 'Bleeding Disorder', hi: 'रक्तस्राव विकार' },
  ];

  const habitsOptions = [
    { en: 'Smoking', hi: 'धूम्रपान' },
    { en: 'Tobacco', hi: 'तंबाकू' },
    { en: 'Alcohol', hi: 'शराब' },
    { en: 'Other', hi: 'अन्य' },
  ];

  const patientMedical = patient.medicalHistory || [];
  const patientHabits = patient.habits || [];
  const patientMedicines = (patient.currentMedicines || []).join(', ');
  const patientAllergies = (patient.allergies || []).join(', ');

  // Check if a medical condition is selected
  const isMedChecked = (en) => patientMedical.some(m => m.toLowerCase().includes(en.toLowerCase().split('/')[0].split(' ')[0].toLowerCase()));
  const otherMedical = patientMedical.filter(m => !medConditions.some(c => isMedChecked(c.en) && m.toLowerCase().includes(c.en.toLowerCase().split('/')[0].split(' ')[0].toLowerCase())));

  const cb = (checked) => checked
    ? '<span style="display:inline-block;width:13px;height:13px;border:1.5px solid #000;background:#000;color:#fff;text-align:center;font-size:10px;line-height:13px;margin-right:3px;">✓</span>'
    : '<span style="display:inline-block;width:13px;height:13px;border:1.5px solid #000;margin-right:3px;"></span>';

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Registration Form - ${patient.name}</title>
      <style>
        @page { size: A4; margin: 12mm; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Noto Sans Devanagari', Arial, sans-serif; color: #000; font-size: 12px; line-height: 1.4; }
        .form-box { max-width: 750px; margin: 0 auto; border: 2px solid #000; }
        .header { text-align: center; padding: 10px 20px; border-bottom: 2px solid #000; }
        .clinic-name { font-size: 20px; font-weight: bold; letter-spacing: 1px; }
        .clinic-sub { font-size: 10px; color: #444; margin-top: 2px; }
        .form-title { font-size: 14px; font-weight: bold; text-align: center; padding: 6px; border-bottom: 2px solid #000; background: #f0f0f0; letter-spacing: 1px; }
        .section { padding: 10px 18px; border-bottom: 1px solid #000; }
        .section:last-child { border-bottom: none; }
        .section-title { font-size: 12px; font-weight: bold; margin-bottom: 8px; text-decoration: underline; }
        .field-row { display: flex; gap: 10px; margin-bottom: 6px; align-items: baseline; flex-wrap: wrap; }
        .field-label { font-weight: 600; min-width: 135px; font-size: 11px; }
        .field-value { border-bottom: 1px dotted #000; flex: 1; min-width: 100px; padding: 1px 4px; font-size: 12px; min-height: 16px; }
        .cb-row { display: flex; flex-wrap: wrap; gap: 10px; margin: 5px 0; }
        .cb-item { display: inline-flex; align-items: center; gap: 3px; font-size: 11px; }
        .consent-hi { font-size: 11px; line-height: 1.6; }
        .consent-en { font-size: 10.5px; line-height: 1.5; color: #333; }
        .sign-row { display: flex; justify-content: space-between; margin-top: 18px; }
        .sign-line { border-top: 1px solid #000; width: 170px; margin-top: 35px; padding-top: 3px; font-size: 10px; text-align: center; }
        .office-box { border-top: 2px solid #000; padding: 8px 18px; background: #f9f9f9; font-size: 11px; }
        .photo-box { width: 90px; height: 110px; border: 1px solid #000; display: flex; align-items: center; justify-content: center; font-size: 9px; color: #999; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style></head><body>${printRef.current.innerHTML}
      <script>window.onload=function(){window.print();window.close();}<\/script>
      </body></html>`);
    w.document.close();
  };

  // Build checkbox HTML strings
  const medCheckboxes = medConditions.map(c => {
    const checked = isMedChecked(c.en);
    return `<span class="cb-item">${cb(checked)} ${c.hi} (${c.en})</span>`;
  }).join('');

  const habitsCheckboxes = habitsOptions.map(h => {
    const checked = patientHabits.includes(h.en);
    return `<span class="cb-item">${cb(checked)} ${h.hi} (${h.en})</span>`;
  }).join('');

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ backgroundColor: 'white', borderRadius: '12px', width: '870px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 24px', borderBottom: '1px solid #eee', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10, borderRadius: '12px 12px 0 0' }}>
          <h3 style={{ margin: 0, color: '#1a2332' }}>📋 Registration Form / पंजीकरण प्रपत्र</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handlePrint} style={{ padding: '8px 22px', backgroundColor: '#00B5B8', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}>🖨 Print</button>
            <button onClick={onClose} style={{ padding: '8px 16px', backgroundColor: '#f0f0f0', color: '#555', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Close</button>
          </div>
        </div>

        {/* Form content (this div is what gets printed) */}
        <div ref={printRef} style={{ padding: '24px' }}>
          <div className="form-box" style={{ maxWidth: '750px', margin: '0 auto', border: '2px solid #000' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '10px 18px', borderBottom: '2px solid #000', gap: 10 }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', letterSpacing: '1px' }}>{clinic?.name || 'SIDDHARTH DENTAL HOSPITAL'}</div>
                <div style={{ fontSize: '10px', color: '#444', marginTop: '2px' }}>ॐ सर्वे भवन्तु सुखिनः सर्वे सन्तु निरामयाः।</div>
                <div style={{ fontSize: '10px', color: '#555' }}>{clinic?.address || 'Mata ji Market, Shahpura 311404'} | {clinic?.phone || '8233994507'} | sdhdental0@gmail.com</div>
              </div>
              {patient._id && (
                <div style={{ textAlign: 'center', flexShrink: 0 }} title="Scan from mobile app to open profile">
                  <QRCodeSVG value={`oralzy://patient/${patient._id}`} size={58} level="M" includeMargin={false} />
                  <div style={{ fontSize: 8, color: '#666', marginTop: 1, fontWeight: 600 }}>SCAN</div>
                </div>
              )}
            </div>

            {/* Form Title */}
            <div style={{ fontSize: '14px', fontWeight: 'bold', textAlign: 'center', padding: '6px', borderBottom: '2px solid #000', background: '#f0f0f0', letterSpacing: '1px' }}>
              पंजीकरण प्रपत्र / REGISTRATION FORM
            </div>

            {/* Section 1: Personal Information */}
            <div style={{ padding: '10px 18px', borderBottom: '1px solid #000' }}>
              <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', textDecoration: 'underline' }}>1. व्यक्तिगत जानकारी / Personal Information</div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '6px', alignItems: 'baseline' }}>
                    <span style={{ fontWeight: 600, minWidth: '135px', fontSize: '11px' }}>UHID No.:</span>
                    <span style={{ borderBottom: '1px dotted #000', flex: 1, padding: '1px 4px', fontSize: '13px', fontWeight: 'bold' }}>{patient.uhid || ''}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '6px', alignItems: 'baseline' }}>
                    <span style={{ fontWeight: 600, minWidth: '135px', fontSize: '11px' }}>नाम / Name:</span>
                    <span style={{ borderBottom: '1px dotted #000', flex: 1, padding: '1px 4px', fontSize: '12px' }}>{(patient.title ? patient.title + ' ' : '') + patient.name}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '6px', alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, minWidth: '70px', fontSize: '11px' }}>आयु / Age:</span>
                    <span style={{ borderBottom: '1px dotted #000', minWidth: '60px', padding: '1px 4px', fontSize: '12px' }}>{patient.age || ''} yrs</span>
                    <span style={{ fontWeight: 600, minWidth: '70px', fontSize: '11px' }}>लिंग / Sex:</span>
                    <span style={{ borderBottom: '1px dotted #000', minWidth: '60px', padding: '1px 4px', fontSize: '12px' }}>{patient.gender || ''}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '6px', alignItems: 'baseline' }}>
                    <span style={{ fontWeight: 600, minWidth: '135px', fontSize: '11px' }}>पिता/पति का नाम /<br/>Father's/Husband's Name:</span>
                    <span style={{ borderBottom: '1px dotted #000', flex: 1, padding: '1px 4px', fontSize: '12px' }}>{patient.fatherHusbandName || ''}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '6px', alignItems: 'baseline' }}>
                    <span style={{ fontWeight: 600, minWidth: '135px', fontSize: '11px' }}>पता / Address:</span>
                    <span style={{ borderBottom: '1px dotted #000', flex: 1, padding: '1px 4px', fontSize: '12px' }}>{patient.address || ''}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '6px', alignItems: 'baseline' }}>
                    <span style={{ fontWeight: 600, minWidth: '135px', fontSize: '11px' }}>मोबाइल नं. / Mobile No.:</span>
                    <span style={{ borderBottom: '1px dotted #000', flex: 1, padding: '1px 4px', fontSize: '12px' }}>{patient.phone || ''}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '6px', alignItems: 'baseline' }}>
                    <span style={{ fontWeight: 600, minWidth: '135px', fontSize: '11px' }}>आधार सं. / Aadhar No.:</span>
                    <span style={{ borderBottom: '1px dotted #000', flex: 1, padding: '1px 4px', fontSize: '12px' }}>{patient.aadhaar || ''}</span>
                  </div>
                </div>
                <div style={{ flexShrink: 0 }}>
                  <div style={{ width: '90px', height: '110px', border: '1px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: '#999' }}>
                    PHOTO
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Medical History */}
            <div style={{ padding: '10px 18px', borderBottom: '1px solid #000' }}>
              <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', textDecoration: 'underline' }}>2. चिकित्सा इतिहास / Medical History</div>
              <div style={{ fontSize: '11px', marginBottom: '5px' }}>क्या आपको वर्तमान में या पहले कोई गंभीर बीमारी रही है? / Have you had any serious illness?</div>
              <div className="cb-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', margin: '5px 0' }} dangerouslySetInnerHTML={{ __html: medCheckboxes }} />
              <div style={{ display: 'flex', gap: '10px', marginTop: '6px', marginBottom: '6px', alignItems: 'baseline' }}>
                <span style={{ fontSize: '11px' }}>अन्य / Other:</span>
                <span style={{ borderBottom: '1px dotted #000', flex: 1, padding: '1px 4px', fontSize: '11px' }}>{otherMedical.join(', ')}</span>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '5px', alignItems: 'baseline' }}>
                <span style={{ fontWeight: 600, fontSize: '11px' }}>क्या आप कोई दवा ले रहे हैं? / Are you taking any medication?</span>
                <span style={{ borderBottom: '1px dotted #000', flex: 1, padding: '1px 4px', fontSize: '11px' }}>{patientMedicines}</span>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '5px', alignItems: 'baseline' }}>
                <span style={{ fontWeight: 600, fontSize: '11px' }}>क्या आपको किसी चीज़ से एलर्जी है? / Are you allergic to anything?</span>
                <span style={{ borderBottom: '1px dotted #000', flex: 1, padding: '1px 4px', fontSize: '11px' }}>{patientAllergies}</span>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '11px' }}>क्या आप गर्भवती हैं? / Pregnant?</span>
                <span className="cb-item" style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px' }} dangerouslySetInnerHTML={{ __html: cb(patient.isPregnant === 'Yes') + ' हाँ/Yes' }} />
                <span className="cb-item" style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px' }} dangerouslySetInnerHTML={{ __html: cb(patient.isPregnant === 'No') + ' नहीं/No' }} />
                <span style={{ margin: '0 6px', color: '#999' }}>|</span>
                <span style={{ fontSize: '11px' }}>स्तनपान? / Breastfeeding?</span>
                <span className="cb-item" style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px' }} dangerouslySetInnerHTML={{ __html: cb(patient.isBreastfeeding === 'Yes') + ' हाँ/Yes' }} />
                <span className="cb-item" style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px' }} dangerouslySetInnerHTML={{ __html: cb(patient.isBreastfeeding === 'No') + ' नहीं/No' }} />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '6px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px' }}>आदतें / Habits:</span>
                <span dangerouslySetInnerHTML={{ __html: habitsCheckboxes }} style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }} />
              </div>
            </div>

            {/* Section 3: Declaration & Consent */}
            <div style={{ padding: '10px 18px', borderBottom: '1px solid #000' }}>
              <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', textDecoration: 'underline' }}>3. घोषणा और सहमति / Declaration & Consent</div>
              <div style={{ fontSize: '11px', lineHeight: '1.6', marginBottom: '5px' }}>
                मैं घोषणा करता/करती हूँ कि ऊपर दी गई जानकारी मेरी सर्वोत्तम जानकारी के अनुसार सत्य है। मैं सिद्धार्थ डेंटल हॉस्पिटल के डॉक्टरों को मेरी जाँच, निदान और आवश्यक दंत उपचार (लोकल एनेस्थीसिया सहित) करने की सहमति देता/देती हूँ। मैं अस्पताल के नियमों का पालन करने और उपचार शुल्क का भुगतान करने के लिए सहमत हूँ।
              </div>
              <div style={{ fontSize: '10.5px', lineHeight: '1.5', color: '#333' }}>
                I declare that the information provided above is true to the best of my knowledge. I consent to the doctors of Siddharth Dental Hospital to perform examination, diagnosis, and necessary dental treatment (including local anesthesia). I agree to follow hospital rules and pay per the treatment fees.
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '18px' }}>
                <div><span style={{ fontSize: '11px' }}>दिनांक / Date: </span><strong style={{ fontSize: '12px' }}>{fmtDate(patient.createdAt || patient.patientSince)}</strong></div>
                <div style={{ textAlign: 'center', width: '170px' }}>
                  <div style={{ borderTop: '1px solid #000', marginTop: '35px', paddingTop: '3px', fontSize: '10px' }}>रोगी के हस्ताक्षर / Patient's Signature</div>
                </div>
              </div>
            </div>

            {/* Office Use Only */}
            <div style={{ borderTop: '2px solid #000', padding: '8px 18px', background: '#f9f9f9', fontSize: '11px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>कार्यालय उपयोग के लिए / FOR OFFICE USE ONLY:</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                <span>पंजीकरण शुल्क / Registration Fee: ₹________</span>
                <span>प्राप्तकर्ता के हस्ताक्षर / Receiver's Signature: ________</span>
              </div>
              <div style={{ marginTop: '4px' }}>अभिभावक का नाम व संबंध (यदि नाबालिग हो) / Guardian's Name & Relation (if minor): ________________</div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationFormPrint;
